import React, { useEffect, useMemo, useState } from "react";
import {
  TextField,
  MenuItem,
  Select,
  Box,
  Typography,
  Button,
  Pagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Autocomplete,
  FormControl,
  InputLabel,
  Stack,
  TableSortLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloseIcon from "@mui/icons-material/Close";
import {
  LuBriefcase,
  LuBuilding2,
  LuDownload,
  LuGem,
  LuLandmark,
  LuGlobe,
  LuShield,
  LuPackage,
  LuLayers,
  LuPiggyBank,
  LuScrollText,
  LuTrendingUp,
} from "react-icons/lu";
import { IconType } from "react-icons";
import { useDispatch } from "react-redux";
import { getAllUserForRM, getUser } from "../../redux/user/userSlice";
import { investorReports } from "../../redux/Reports/reportsSlice";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AppLoader from "../../components/AppLoader/AppLoader";
import toast from "react-hot-toast";
import MfInvestorReportPanel from "./mf/MfInvestorReportPanel";
import EquityInvestorReportPanel from "./equity/EquityInvestorReportPanel";
import BondInvestorReportPanel from "./bond/BondInvestorReportPanel";
import AifInvestorReportPanel from "./aif/AifInvestorReportPanel";
import PmsInvestorReportPanel from "./pms/PmsInvestorReportPanel";
import FdInvestorReportPanel from "./fd/FdInvestorReportPanel";
import SifInvestorReportPanel from "./sif/SifInvestorReportPanel";
import IntlEquityInvestorReportPanel from "./intlEquity/IntlEquityInvestorReportPanel";
import ReitInvestorReportPanel from "./reit/ReitInvestorReportPanel";
import InsuranceInvestorReportPanel from "./insurance/InsuranceInvestorReportPanel";
import OtherAssetInvestorReportPanel from "./otherAsset/OtherAssetInvestorReportPanel";
import PrivateEquityInvestorReportPanel from "./privateEquity/PrivateEquityInvestorReportPanel";
import {
  REPORT_CARD_CONTENT_SX,
  REPORT_CARD_SX,
  REPORT_PAGE_INNER_SX,
  REPORT_PAGE_SX,
  REPORT_TYPO,
} from "./mf/mfReportsTheme";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  OFF_WHITE,
  PROFILE_FONT,
  SIDEBAR_BG,
  filterFieldSx,
} from "../../constants/layout";
import {
  cardSx,
  primaryButtonSx,
  schemeHeadCellSx,
} from "../../components/AssetListing/assetListingShared";

const ICON_BLUE = INPUT_BORDER_FOCUS;

const ASSET_CLASS_OPTIONS: { value: string; label: string; icon: IconType }[] = [
  { value: "MUTUAL_FUND", label: "Mutual Fund", icon: LuLandmark },
  { value: "AIF", label: "AIF", icon: LuBuilding2 },
  { value: "PMS", label: "PMS", icon: LuBriefcase },
  { value: "SIF", label: "SIF", icon: LuLayers },
  { value: "STOCK", label: "Equity", icon: LuTrendingUp },
  { value: "PRIVATE_STOCK", label: "Unlisted Equity", icon: LuGem },
  { value: "INTERNATIONAL_EQUITY", label: "International Equity", icon: LuGlobe },
  { value: "REIT", label: "REIT/INVIT", icon: LuGlobe },
  { value: "INSURANCE", label: "Insurance", icon: LuShield },
  { value: "OTHER", label: "Other Asset", icon: LuPackage },
  { value: "BOND", label: "Bond", icon: LuScrollText },
  { value: "FD", label: "Fixed Deposit", icon: LuPiggyBank },
];

const QUICK_REPORT_OPTIONS = ASSET_CLASS_OPTIONS;

interface User {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  mobile: string;
  logInBy: number;
  onboardStatus: number;
  createdAt: string;
  aumUserId: string;
}

const ITEMS_PER_PAGE = 10;

const assetClassTableConfig: Record<
  string,
  { label: string; key: string; sort: boolean }[]
> = {
  MUTUAL_FUND: [
    { label: "Investor Name", key: "userName", sort: false },
    { label: "Aum UserId", key: "aumUserId", sort: false },
    { label: "Scheme Name", key: "schemeName", sort: false },
    { label: "Units", key: "units", sort: false },
    { label: "Investment Amount (In Rs.)", key: "invested", sort: true },
    {
      label: "Market / Maturity Value (In Rs.)",
      key: "currentValue",
      sort: true,
    },
    {
      label: "Unrealised Gain/Loss (In %)",
      key: "gainLossPercentage",
      sort: true,
    },
    { label: "Folio Number / FDR", key: "folioNo", sort: false },
    { label: "Scheme Category", key: "schemeType", sort: false },
  ],
  AIF: [
    { label: "Investor Name", key: "userName", sort: false },
    { label: "Aum UserId", key: "aumUserId", sort: false },
    { label: "Scheme Name", key: "schemeName", sort: false },
    { label: "Investment Amount (In Rs.)", key: "invested", sort: true },
    {
      label: "Market / Maturity Value (In Rs.)",
      key: "currentValue",
      sort: true,
    },
    { label: "Unrealised Gain/Loss (In %)", key: "gainLoss", sort: true },
    { label: "Folio Number / FDR", key: "folioNo", sort: false },
  ],
  PMS: [
    { label: "Investor Name", key: "userName", sort: false },
    { label: "Aum UserId", key: "aumUserId", sort: false },
    { label: "Scheme Name", key: "schemeName", sort: false },
    { label: "Investment Amount (In Rs.)", key: "invested", sort: true },
    {
      label: "Market / Maturity Value (In Rs.)",
      key: "currentValue",
      sort: true,
    },
    { label: "Unrealised Gain/Loss (In %)", key: "gainLoss", sort: true },
    { label: "Folio Number / FDR", key: "folioNo", sort: false },
  ],
  STOCK: [
    { label: "Investor Name", key: "userName", sort: false },
    { label: "Aum UserId", key: "aumUserId", sort: false },
    { label: "Scheme Name", key: "schemeName", sort: false },
    { label: "Units", key: "units", sort: true },
    { label: "Investment Amount (In Rs.)", key: "invested", sort: true },
    {
      label: "Market / Maturity Value (In Rs.)",
      key: "currentValue",
      sort: true,
    },
    {
      label: "Unrealised Gain/Loss (In %)",
      key: "gainLossPercentage",
      sort: true,
    },
  ],
  PRIVATE_STOCK: [
    { label: "Investor Name", key: "userName", sort: false },
    { label: "Aum UserId", key: "aumUserId", sort: false },
    { label: "Scheme Name", key: "schemeName", sort: false },
    { label: "Units", key: "units", sort: true },
    { label: "Investment Amount (In Rs.)", key: "invested", sort: true },
    {
      label: "Market / Maturity Value (In Rs.)",
      key: "currentValue",
      sort: true,
    },
    {
      label: "Unrealised Gain/Loss (In %)",
      key: "gainLossPercentage",
      sort: true,
    },
  ],
  BOND: [
    { label: "Investor Name", key: "userName", sort: false },
    { label: "Aum UserId", key: "aumUserId", sort: false },
    { label: "Bond Name", key: "schemeName", sort: false },
    { label: "Units", key: "units", sort: true },
    { label: "Investment Amount (In Rs.)", key: "invested", sort: true },
    {
      label: "Settlement Amount (In Rs.)",
      key: "currentValue",
      sort: true,
    },
    { label: "Yield", key: "yield", sort: false },
    { label: "Bond Source", key: "bondSource", sort: false },
  ],
  FD: [
    { label: "Investor Name", key: "userName", sort: false },
    { label: "Aum UserId", key: "aumUserId", sort: false },
    { label: "Scheme Name", key: "schemeName", sort: false },
    { label: "Investment Amount (In Rs.)", key: "invested", sort: true },
    { label: "Maturity Value (In Rs.)", key: "returnAmount", sort: true },
    { label: "Tenure", key: "tenure", sort: false },
    { label: "Interest Rate", key: "interesetRate", sort: true },
    { label: "Folio Number / FDR", key: "folioNo", sort: false },
  ],
};

const InvestorReport = () => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [assetClass, setAssetClass] = useState("MUTUAL_FUND");
  const [selectedInvestorIds, setSelectedInvestorIds] = useState<number[]>([]);
  const [selectedInvestor, setSelectedInvestor] = useState<any>(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<{
    email: string;
    contact: string;
  } | null>(null);
  const [showReport, setShowReport] = useState(false);
  const userType = localStorage?.getItem("userType");
  const [users, setUsers] = useState<User[]>([]);
  const dispatch = useDispatch<any>();
  const [searchInvestor, setSearchInvestor] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any>([]);
  const [tableData, setTableData] = useState<any>([]);
  const [tableConfig, setTableConfig] = useState<any>();
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  const canGenerate =
    Boolean(assetClass) && selectedInvestor !== null && selectedInvestor !== undefined;

  useEffect(() => {
    if (searchQuery) {
      const filteredData = data.filter((row: any) =>
        row.schemeName.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setTableData(filteredData);
    } else {
      setTableData(data);
    }
  }, [searchQuery]);

  const handleOpenModal = (contact: string, email: string) => {
    setSelectedContact({ contact, email });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedContact(null);
  };

  const handlePageChange = (_: any, value: number) => {
    setPage(value);
  };

  const paginatedData = tableData.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  useEffect(() => {
    fetchUsers();
  }, []);

  const sortedData = useMemo(() => {
    if (!sortConfig) return paginatedData;

    const sorted = [...paginatedData].sort((a, b) => {
      const aValue = a[sortConfig.key] ?? 0;
      const bValue = b[sortConfig.key] ?? 0;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }
      return 0;
    });
    return sorted;
  }, [paginatedData, sortConfig]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response =
        userType == "4"
          ? await dispatch(
              getUser({
                pageIndex: 1,
                pageSize: 1000,
                userType: "4",
              })
            )
          : await dispatch(
              getAllUserForRM({
                pageIndex: 1,
                pageSize: 1000,
                forRm: userType === "2" ? 1 : userType === "6" ? 0 : -1,
                sortBy: 4,
                onboardStatus: -1,
                startDate: "",
                endDate: "",
                search: "",
              })
            );
      const userData = response.payload.data || [];
      setUsers(userData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Error fetching user data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        investorReports({
          userType: userType,
          holdingType: assetClass,
          mfCategory: "",
          forRm: userType === "2" ? 1 : userType === "6" ? 0 : -1,
          pageSize: 1000,
          pageIndex: 1,
          rmId: localStorage?.getItem("ID"),
          userIds:
            selectedInvestor === 0 || !selectedInvestor
              ? []
              : [selectedInvestor],
          search: "",
        })
      );
      setTableConfig(assetClassTableConfig[assetClass]);

      const resData =
        response.payload.data?.data?.map((item: any, index: number) => {
          item.id = index + 1;
          return item;
        }) || [];
      setData(resData);
      setTableData(resData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      toast.error("Error fetching report data");
    } finally {
      setIsLoading(false);
      setShowReport(true);
    }
  };

  const generatePDF = (data: any[]) => {
    const doc = new jsPDF({
      orientation: "landscape", // default is 'portrait'
    });

    if (data.length === 0) {
      doc.text("No data available", 10, 10);
      doc.save(`${assetClass}.pdf`);
      return;
    }

    // Get columns from keys (you can customize)
    const columns = Object.keys(data[0]).map((key) => ({
      header: key,
      dataKey: key,
    }));

    autoTable(doc, {
      columns,
      body: data,
      styles: {
        fontSize: 7,
        cellPadding: 1,
      },
      headStyles: {
        fillColor: [22, 160, 133],
        fontSize: 8,
      },
    });

    doc.save(`${assetClass}.pdf`);
  };

  const generateCSV = (data: any[]) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${assetClass}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleDownload = (fileType: string) => {
    let data = tableConfig?.map((col: any) => col.key);
    data = [...data, "email", "mobile"];
    const newTableData = tableData.map((row: any) => {
      const newRow: any = {};
      data.forEach((key: any) => {
        newRow[key] = row[key];
      });
      return newRow;
    });
    fileType === "pdf" ? generatePDF(newTableData) : generateCSV(newTableData);
  };

  const handleSort = (key: string) => {
    setSortConfig((prevSortConfig) => {
      if (prevSortConfig?.key === key) {
        return {
          key,
          direction: prevSortConfig.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const headCellSx = { ...schemeHeadCellSx, textAlign: "center" as const };

  const stickyColWidths = [160, 140];

  const getStickyColumnSx = (colIndex: number, variant: "head" | "body") => {
    if (colIndex > 1) return {};

    const left = colIndex === 0 ? 0 : stickyColWidths[0];
    const width = stickyColWidths[colIndex];

    return {
      position: "sticky" as const,
      left,
      zIndex: variant === "head" ? 6 : 3,
      minWidth: width,
      width,
      maxWidth: width,
      ...(variant === "head" && { bgcolor: SIDEBAR_BG }),
      ...(variant === "body" && {
        bgcolor: "#ffffff",
        ".MuiTableRow-root:hover &": { bgcolor: "#FAFAFA !important" },
      }),
    };
  };

  const bodyCellSx = {
    fontFamily: PROFILE_FONT,
    fontSize: 14,
    color: DARK_NAVY,
    textAlign: "center" as const,
    borderBottom: "1px solid #F5F5F5",
    py: 1.75,
    bgcolor: "#ffffff",
    ".MuiTableRow-root:hover &": { bgcolor: "#FAFAFA" },
  };

  const usesModernReportPanel =
    assetClass === "MUTUAL_FUND" ||
    assetClass === "STOCK" ||
    assetClass === "PRIVATE_STOCK" ||
    assetClass === "BOND" ||
    assetClass === "AIF" ||
    assetClass === "PMS" ||
    assetClass === "SIF" ||
    assetClass === "FD" ||
    assetClass === "INTERNATIONAL_EQUITY" ||
    assetClass === "REIT" ||
    assetClass === "INSURANCE" ||
    assetClass === "OTHER";

  return (
    <>
      <AppLoader loading={isLoading} />
      <Box
        sx={{
          ...(usesModernReportPanel ? REPORT_PAGE_SX : {
            minHeight: "100%",
            bgcolor: OFF_WHITE,
            px: { xs: 3, sm: 5, md: 6, lg: 8 },
            py: { xs: 3, md: 4 },
            fontFamily: PROFILE_FONT,
          }),
        }}
      >
        <Box sx={usesModernReportPanel ? REPORT_PAGE_INNER_SX : { maxWidth: 1280, mx: "auto" }}>
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <Typography
              sx={
                usesModernReportPanel
                  ? {
                      ...REPORT_TYPO.sectionHeading,
                      fontSize: { xs: 20, sm: 22, md: 26, lg: 32 },
                      fontWeight: 700,
                      letterSpacing: -0.5,
                    }
                  : {
                      fontFamily: PROFILE_FONT,
                      fontSize: { xs: 24, md: 28 },
                      fontWeight: 700,
                      color: DARK_NAVY,
                    }
              }
            >
              Investor Reports
            </Typography>
          </Box>

          <Box
            sx={{
              ...(usesModernReportPanel
                ? { ...REPORT_CARD_SX, ...REPORT_CARD_CONTENT_SX, mb: { xs: 2, sm: 3, lg: 5 } }
                : { ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }),
            }}
          >
            {assetClass === "MUTUAL_FUND" ? (
              <MfInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "STOCK" ? (
              <EquityInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "PRIVATE_STOCK" ? (
              <PrivateEquityInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "BOND" ? (
              <BondInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "AIF" ? (
              <AifInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "PMS" ? (
              <PmsInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "SIF" ? (
              <SifInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "FD" ? (
              <FdInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "INTERNATIONAL_EQUITY" ? (
              <IntlEquityInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "REIT" ? (
              <ReitInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "INSURANCE" ? (
              <InsuranceInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : assetClass === "OTHER" ? (
              <OtherAssetInvestorReportPanel
                users={users}
                userType={userType}
                assetClass={assetClass}
                onAssetClassChange={setAssetClass}
                selectedInvestorIds={selectedInvestorIds}
                onSelectedInvestorIdsChange={setSelectedInvestorIds}
              />
            ) : (
            <>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 18,
                fontWeight: 700,
                color: DARK_NAVY,
                mb: 0.5,
              }}
            >
              Custom Report Builder
            </Typography>
            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 14,
                color: DARK_NAVY_LIGHT,
                mb: 3,
              }}
            >
              Configure investor, asset class, and category for export-ready reports.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
                mb: 4,
              }}
            >
              <Autocomplete
                freeSolo
                disableClearable
                options={users?.map((user) => ({
                  label: user.firstName + " (" + user.aumUserId + ")",
                  value: user.userId || user.id,
                }))}
                filterOptions={(options, state) => {
                  const inputValue = state.inputValue.toLowerCase();
                  const filtered = options.filter((option) =>
                    option.label.toLowerCase().includes(inputValue)
                  );
                  const allInvestor = { label: "All Investor", value: 0 };
                  const includeAllInvestor =
                    inputValue === "" || "all investor".includes(inputValue);
                  return includeAllInvestor ? [allInvestor, ...filtered] : filtered;
                }}
                inputValue={searchInvestor}
                onInputChange={(_, newInputValue, reason) => {
                  setSearchInvestor(newInputValue);
                  if (reason === "clear" || newInputValue === "") {
                    setSelectedInvestor(null);
                  }
                }}
                getOptionLabel={(option: any) =>
                  typeof option === "string" ? option : option?.label || ""
                }
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  return typeof value === "string"
                    ? option.label === value
                    : option.value === value.value;
                }}
                value={
                  selectedInvestor === null || selectedInvestor === 0
                    ? undefined
                    : users
                        .map((user) => ({
                          label: user.firstName + " (" + user.aumUserId + ")",
                          value: user.userId || user.id,
                        }))
                        .find((opt) => opt.value === selectedInvestor)
                }
                onChange={(_, newValue: any) => {
                  if (!newValue) {
                    setSelectedInvestor(null);
                    setSearchInvestor("");
                  } else if (typeof newValue === "string") {
                    setSelectedInvestor(null);
                    setSearchInvestor(newValue);
                  } else if (newValue) {
                    setSelectedInvestor(newValue.value);
                    setSearchInvestor(newValue.label);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Investor Name"
                    variant="outlined"
                    sx={filterFieldSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {selectedInvestor !== null && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedInvestor(null);
                                setSearchInvestor("");
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                )}
              />

              <FormControl fullWidth sx={filterFieldSx}>
                <InputLabel id="segment-label">Asset Class</InputLabel>
                <Select
                  labelId="segment-label"
                  value={assetClass}
                  onChange={(e) => {
                    setAssetClass(e.target.value);
                  }}
                  label="Asset Class"
                >
                  <MenuItem value="">Select Asset Class</MenuItem>
                  {ASSET_CLASS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Typography
              sx={{
                fontFamily: PROFILE_FONT,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.08em",
                color: DARK_NAVY_LIGHT,
                textTransform: "uppercase",
                mb: 1.5,
              }}
            >
              Quick Reports
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.25,
                mb: 3,
              }}
            >
              {QUICK_REPORT_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const isSelected = assetClass === opt.value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => {
                      setAssetClass(opt.value);
                    }}
                    sx={{
                      width: 88,
                      border: isSelected
                        ? `2px solid ${ICON_BLUE}`
                        : "1px solid #E8E8E8",
                      borderRadius: "10px",
                      bgcolor: isSelected ? "rgba(30, 58, 95, 0.04)" : "#ffffff",
                      px: 1,
                      py: 1.25,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      minHeight: 72,
                      cursor: "pointer",
                      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        borderColor: ICON_BLUE,
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "8px",
                        bgcolor: isSelected ? ICON_BLUE : "#F5F5F5",
                        color: isSelected ? "#ffffff" : ICON_BLUE,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Icon size={15} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: PROFILE_FONT,
                        fontSize: 10,
                        fontWeight: 600,
                        color: DARK_NAVY,
                        textAlign: "center",
                        lineHeight: 1.2,
                      }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
              <Button
                variant="contained"
                disableElevation
                disabled={!canGenerate}
                onClick={handleGenerateReport}
                sx={{
                  ...primaryButtonSx,
                  py: 1.5,
                  px: 4,
                  fontSize: 16,
                  fontWeight: 700,
                  borderRadius: "10px",
                  minWidth: 220,
                }}
              >
                Generate Report
              </Button>
            </Box>

          {showReport && (
            <>
              <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  justifyContent="space-between"
                >
                  <TextField
                    variant="outlined"
                    placeholder="Search by Scheme Name"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(1);
                    }}
                    size="small"
                    sx={{ ...filterFieldSx, maxWidth: { sm: 420 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: DARK_NAVY_LIGHT }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      disableElevation
                      startIcon={<LuDownload size={16} />}
                      onClick={() => handleDownload("pdf")}
                      sx={{
                        ...primaryButtonSx,
                        py: 0.875,
                        px: 2,
                        fontSize: 13,
                      }}
                    >
                      PDF
                    </Button>
                    <Button
                      variant="contained"
                      disableElevation
                      startIcon={<LuDownload size={16} />}
                      onClick={() => handleDownload("csv")}
                      sx={{
                        ...primaryButtonSx,
                        py: 0.875,
                        px: 2,
                        fontSize: 13,
                      }}
                    >
                      CSV
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ ...cardSx, overflow: "hidden" }}>
                <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
                  <Table stickyHeader sx={{ minWidth: 1200 }}>
                    <TableHead>
                      <TableRow>
                        {tableConfig?.map((col: any, idx: number) => (
                          <TableCell
                            key={idx}
                            sx={{
                              ...headCellSx,
                              ...getStickyColumnSx(idx, "head"),
                              minWidth: idx < 2 ? stickyColWidths[idx] : 140,
                              cursor: col.sort ? "pointer" : "default",
                            }}
                            sortDirection={
                              sortConfig && sortConfig.key === col.key
                                ? sortConfig.direction
                                : undefined
                            }
                          >
                            {col.sort ? (
                              <TableSortLabel
                                active={sortConfig?.key === col.key}
                                direction={
                                  sortConfig && sortConfig.key === col.key
                                    ? sortConfig.direction
                                    : "asc"
                                }
                                onClick={() => handleSort(col.key)}
                                sx={{
                                  color: "inherit !important",
                                  "& .MuiTableSortLabel-icon": { color: "inherit !important" },
                                }}
                              >
                                {col.label}
                              </TableSortLabel>
                            ) : (
                              col.label
                            )}
                          </TableCell>
                        ))}
                        <TableCell sx={{ ...headCellSx, width: 88 }}>Contact</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedData?.map((row: any, index: number) => (
                        <TableRow key={index} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                          {tableConfig?.map((col: any, idx: number) => (
                            <TableCell
                              key={idx}
                              sx={{
                                ...bodyCellSx,
                                ...getStickyColumnSx(idx, "body"),
                              }}
                            >
                              {typeof row[col.key] === "number"
                                ? Number(row[col.key]).toFixed(2)
                                : row[col.key] ?? "-"}
                            </TableCell>
                          ))}
                          <TableCell sx={bodyCellSx}>
                            <IconButton
                              onClick={() => handleOpenModal(row.mobile, row.email)}
                              size="small"
                              sx={{ color: ICON_BLUE }}
                            >
                              <AccountCircleIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box mt={4} display="flex" justifyContent="center">
                <Pagination
                  count={Math.ceil(tableData.length / ITEMS_PER_PAGE)}
                  page={page}
                  onChange={handlePageChange}
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      fontFamily: PROFILE_FONT,
                      color: DARK_NAVY_LIGHT,
                    },
                    "& .Mui-selected": {
                      bgcolor: `${ICON_BLUE} !important`,
                      color: "#ffffff !important",
                    },
                  }}
                />
              </Box>
            </>
          )}
            </>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        PaperProps={{ sx: { borderRadius: "16px", minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontFamily: PROFILE_FONT, fontWeight: 700, color: DARK_NAVY }}>
          Investor Contact Details
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY }}>
            <strong>Contact Number:</strong> {selectedContact?.contact}
          </Typography>
          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY, mt: 1 }}>
            <strong>Email:</strong> {selectedContact?.email}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              fontFamily: PROFILE_FONT,
              textTransform: "none",
              fontWeight: 600,
              color: DARK_NAVY_LIGHT,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvestorReport;
